// useFilterFilters.tsx
import { useMemo, useState } from 'react';
import { Filter } from '@ais-ui/websockets';
import { FilterFilters, FilterSortConfig } from './types';
import { useSettings } from '../Settings/SettingsProvider';
import { useFilterContext } from '../Map/FilterContext';
import { isFilterApplied } from './utils';
import { isFilterAppliedOnStartUp } from './isFilterAppliedOnStartUp';
import { getFilterType } from './getFilterType';

export const useFilterFilters = (filters: Filter[], searchTerm: string) => {
  const { settings } = useSettings();
  const { customFilters } = useFilterContext();

  const [filterFilters, setFilterFilters] = useState<FilterFilters>({
    type: 'all',
    owner: 'all',
    favorites: 'all',
    applied: 'all',
  });

  const [sortConfig, setSortConfig] = useState<FilterSortConfig>({
    type: 'name',
    direction: 'asc',
  });

  const filteredAndSortedFilters = useMemo(() => {
    if (!filters) return [];

    // First apply search filter
    let result = [...filters];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (filter) =>
          filter.name.toLowerCase().includes(searchLower) ||
          filter.filter?.toLowerCase().includes(searchLower) ||
          filter.owner?.toLowerCase().includes(searchLower),
      );
    }

    // Apply filters
    result = result.filter((filter) => {
      // Filter by type (using getFilterType)
      if (filterFilters.type !== 'all') {
        const filterType = getFilterType(filter);
        if (filterType.toLowerCase() !== filterFilters.type.toLowerCase())
          return false;
      }

      // Filter by owner
      if (filterFilters.owner !== 'all') {
        if (filter.owner !== filterFilters.owner) return false;
      }

      // Filter by favorites (using isFilterAppliedOnStartUp)
      if (filterFilters.favorites !== 'all') {
        const isFavorited = isFilterAppliedOnStartUp(settings, filter.uuid);
        if (filterFilters.favorites === 'favorited' && !isFavorited)
          return false;
        if (filterFilters.favorites === 'not-favorited' && isFavorited)
          return false;
      }

      // Filter by applied status (using isFilterApplied)
      if (filterFilters.applied !== 'all') {
        const isApplied = isFilterApplied(filter, customFilters);
        if (filterFilters.applied === 'applied' && !isApplied) return false;
        if (filterFilters.applied === 'not-applied' && isApplied) return false;
      }

      return true;
    });

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.type) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'type':
          const typeA = getFilterType(a);
          const typeB = getFilterType(b);
          comparison = typeA.localeCompare(typeB);
          break;
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [filters, searchTerm, filterFilters, sortConfig, settings, customFilters]);

  return {
    filteredAndSortedFilters,
    filterFilters,
    setFilterFilters,
    sortConfig,
    setSortConfig,
  };
};
